import { actions } from "@app/utils";
import {
  createApiClient,
  DEFAULT_API_CLIENT_HEADERS,
} from "@app/utils/apiClient";

export const apiClient = createApiClient(
  actions["exd-storyteller-ai/zoltar"],
  null,
  1,
  DEFAULT_API_CLIENT_HEADERS,
  0,
);

export const isZoltarGenerateOk = (data) =>
  Boolean(data && typeof data === "object" && data.ok === true);

/**
 * Error code returned by the backend while the agent is still producing the
 * response for a given thread. The request has to be repeated with the same
 * thread_id until the response is ready.
 */
export const ZOLTAR_RESPONSE_NOT_READY_CODE = "ZOLTAR_RESPONSE_NOT_READY";

export const ZOLTAR_POLL_USER_INPUT = "Return the data.";

// Stored prompts sent to /imageLabeling instead of /generateContent.
export const IMAGE_LABELING_PROMPT_IDS = [
  "common-image-description",
  "Brand-Visibility-image-description",
  "Brand-Visibility-image-dna",
];

export const isZoltarResponseNotReadyError = (error) => {
  const { status, data } = error?.cause ?? {};
  return (
    data?.code === ZOLTAR_RESPONSE_NOT_READY_CODE ||
    (status === 504 && data?.error === "Response not yet ready.")
  );
};

export const getZoltarGenerateResponseText = (data) => data?.response ?? "";

export const getZoltarGenerateEnvelope = (data) => data?.envelope;

const unwrapAgentAnswerEnvelope = (obj) =>
  obj?.kind === "answer"
    ? JSON.parse(stripZoltarResponseMarkdown(obj.text))
    : obj;

const sanitizeJsonWhitespace = (text) =>
  String(text).replace(/[\u00a0\u202f\u2007\u2060\ufeff]/g, " ");

export const stripZoltarResponseMarkdown = (text) => {
  let t = String(text ?? "").trim();
  if (!t.startsWith("```")) return sanitizeJsonWhitespace(t);
  t = t.replace(/^```(?:json)?\s*/i, "");
  const closing = t.lastIndexOf("```");
  if (closing !== -1) {
    t = t.slice(0, closing).trim();
  }
  return sanitizeJsonWhitespace(t.trim());
};

export const assertZoltarGenerateOk = (
  data,
  errorMessage = "Zoltar generation failed",
) => {
  if (!isZoltarGenerateOk(data)) {
    throw new Error(errorMessage, { cause: data });
  }
};

export const parseZoltarGenerateJson = (data, fallbackJsonText = "{}") => {
  const envelope = getZoltarGenerateEnvelope(data);
  if (envelope) {
    return unwrapAgentAnswerEnvelope(envelope);
  }
  const stripped = stripZoltarResponseMarkdown(
    getZoltarGenerateResponseText(data),
  );
  const raw = stripped.length > 0 ? stripped : fallbackJsonText;
  return unwrapAgentAnswerEnvelope(JSON.parse(raw));
};

export const readZoltarGenerateJsonWithFallback = (
  data,
  fallbackJsonText,
  errorMessage = "Zoltar generation failed",
) => {
  assertZoltarGenerateOk(data, errorMessage);
  return parseZoltarGenerateJson(data, fallbackJsonText);
};

export const parseZoltarGenerateAssistantJson = (
  data,
  errorMessage = "Zoltar generation failed",
) => {
  assertZoltarGenerateOk(data, errorMessage);
  const withThreadId = (result) =>
    result && typeof result === "object" && !Array.isArray(result)
      ? { ...result, thread_id: data?.thread_id }
      : result;
  const envelope = getZoltarGenerateEnvelope(data);
  if (envelope) {
    return withThreadId(unwrapAgentAnswerEnvelope(envelope));
  }
  const text = stripZoltarResponseMarkdown(getZoltarGenerateResponseText(data));
  if (!text.trim()) {
    throw new Error(`${errorMessage}: empty response`, { cause: data });
  }
  return withThreadId(unwrapAgentAnswerEnvelope(JSON.parse(text)));
};

/**
 * Map LangChain-serialized messages from generateContent into { role, prompt_template }[].
 */
export const zoltarLangchainMessagesToPromptTemplate = (messages) => {
  if (!Array.isArray(messages)) return [];
  return messages
    .filter(
      (m) =>
        m &&
        typeof m === "object" &&
        m.kwargs != null &&
        typeof m.kwargs.content === "string",
    )
    .map((m) => {
      const id = m.id;
      const type = Array.isArray(id) ? id[id.length - 1] : "";
      const role = type === "HumanMessage" ? "user" : "system";
      return { role, prompt_template: m.kwargs.content };
    });
};

export const getSystemPrompt = async (systemPromptId, options = {}) => {
  const response = await apiClient.get(`/prompts/${encodeURIComponent(systemPromptId)}`, {}, options);
  if (!response.ok) {
    throw new Error("Failed to load the system prompt. Administrator access is required.", { cause: response });
  }
  return response.data.data;
};

export const listSystemPrompts = async (options = {}) => {
  const response = await apiClient.get("/prompts", {}, options);
  if (!response.ok) {
    throw new Error("Failed to load system prompts. Administrator access is required.", { cause: response });
  }
  return response.data.data;
};

export const getZoltarCurl = async (body, options = {}) => {
  const response = await apiClient.post("/curl", body, options);
  if (!response.ok) {
    throw new Error("Failed to build the cURL command. Administrator access is required.", { cause: response });
  }
  return response.data.data;
};

export const generateContent = async (body, errorMessage, options = {}) => {
  const override = body.promptOverrideKey && localStorage.getItem(body.promptOverrideKey);
  if (override && body.systemPromptId) {
    const document = await getSystemPrompt(body.systemPromptId, options);
    body = {
      ...body,
      systemPromptId: undefined,
      prompt_template: [{ role: "system", prompt_template: override }, ...(body.prompt_template ?? [])],
      schema: document.schema,
    };
  }
  const response = await apiClient.post(
    "/generateContent",
    {
      useAgent: body.useAgent,
      user: body.user,
      data: body?.data || {},
      prompt_template: body.prompt_template, //mergePromptTemplateParts(body.prompt_template),
      ...(body.systemPromptId !== undefined ? { systemPromptId: body.systemPromptId } : {}),
      model: body.model,
      schema: body.schema,
      skills: body.skills,
      thread_id: body.thread_id,
      user_input: body.user_input,
    },
    options,
  );

  if (!response.ok) {
    throw new Error(errorMessage, { cause: response });
  }

  return response.data.data;
};

export const imageLabeling = async (body, options = {}) => {
  const response = await apiClient.post(
    "/imageLabeling",
    {
      imageUrl: body.imageUrl,
      prompt_template: body.prompt_template,
      ...(body.systemPromptId !== undefined ? { systemPromptId: body.systemPromptId } : {}),
      ...(body.userInput !== undefined ? { user_input: body.userInput } : {}),
    },
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to label image", { cause: response });
  }

  return response.data;
};
